import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Container, Paper, Box, Snackbar, Alert, Grid } from '@mui/material';
import ProductSelection from './components/ProductSelection';
import Dimensions from './components/Dimensions';
import ContactForm from './components/ContactForm';
import WizardHeader from './components/WizardHeader';
import SelectionSidebar, { SidebarItem } from './components/SelectionSidebar';
import { submitKonfigurator, trackKonfiguratorEvent } from './services/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1066C6',
      light: '#4286D9',
      dark: '#0B478A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1066C6',
      light: '#4286D9',
      dark: '#0B478A',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f7f7f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1d2430',
      secondary: '#5b6472',
    },
    divider: '#e5e7eb',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
        },
        sizeLarge: {
          padding: '10px 22px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#0B478A',
          },
          '&.Mui-disabled': {
            backgroundColor: '#e2e4e8',
            color: '#9aa1ab',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#ffffff',
        },
      },
    },
  },
});

export type ProductCategory = 'fenster' | 'tueren' | 'lichtschacht';
export type ProductType = 
  | 'spannrahmen_fenster'
  | 'drehfenster' 
  | 'pendelfenster'
  | 'rollo_fenster'
  | 'rollo_dachfenster'
  | 'drehtuer' 
  | 'pendeltuer' 
  | 'plisseetuer' 
  | 'rollo_tuer' 
  | 'schiebetuer'
  | 'lichtschacht';

const categoryTitles: Record<ProductCategory, string> = {
  fenster: 'Fenster',
  tueren: 'Türen',
  lichtschacht: 'Lichtschacht',
};

const productNames: Record<ProductType, string> = {
  spannrahmen_fenster: 'Spannrahmen',
  drehfenster: 'Drehfenster',
  pendelfenster: 'Pendelfenster',
  rollo_fenster: 'Fenster Rollo',
  rollo_dachfenster: 'Dachfenster Rollo',
  drehtuer: 'Drehtür',
  pendeltuer: 'Pendeltür',
  plisseetuer: 'Plisseetür',
  rollo_tuer: 'Tür-Rollo',
  schiebetuer: 'Schiebetür',
  lichtschacht: 'Lichtschacht'
};

const steps = ['Bereiche', 'Produkte', 'Maße', 'Kontakt'];

const App: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState<ProductCategory[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductType[]>([]);
  const [dimensions, setDimensions] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Live-Auswahl für die Sidebar (Auswahl in den Schritten, bevor "Weiter" geklickt wird)
  const [draftCategories, setDraftCategories] = useState<ProductCategory[]>([]);
  const [draftProducts, setDraftProducts] = useState<ProductType[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const configuratorRef = useRef<HTMLDivElement>(null);
  const trackedStepViews = useRef<Set<number>>(new Set());

  const scrollToTop = () => {
    setTimeout(() => {
      if (configuratorRef.current) {
        // Scroll zum Konfigurator-Container mit etwas Offset nach oben
        const yOffset = -20; 
        const element = configuratorRef.current;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
        
        // Fallback für Browser ohne smooth scrolling
        if ('scrollBehavior' in document.documentElement.style === false) {
          window.scrollTo(0, y);
        }
      }
    }, 100);
  };

  const handleCategorySelect = (categories: ProductCategory[]) => {
    trackKonfiguratorEvent('category_selected', 0, {
      categories,
      category_count: categories.length
    });

    setSelectedCategories(categories);
    setDraftProducts([]);
    if (categories.length === 1 && categories[0] === 'lichtschacht') {
      setSelectedProducts(['lichtschacht']);
      trackKonfiguratorEvent('product_selected', 1, {
        products: ['lichtschacht'],
        product_count: 1
      });
      setActiveStep(2);
    } else {
      setActiveStep(1);
    }
    scrollToTop();
  };

  const handleProductSelect = (products: ProductType[]) => {
    trackKonfiguratorEvent('product_selected', 1, {
      products,
      product_count: products.length
    });

    setSelectedProducts(products);
    setActiveStep(2);
    scrollToTop();
  };

  const handleDimensionsSubmit = (values: any) => {
    const products = selectedProducts.map(productId => {
      const quantity = values[`${productId}_quantity`];
      const items = [];
      
      for (let i = 1; i <= quantity; i++) {
        items.push({
          kategorie: selectedCategories.find(cat => {
            if (cat === 'fenster' && ['spannrahmen_fenster', 'drehfenster', 'pendelfenster', 'rollo_fenster', 'rollo_dachfenster'].includes(productId)) return true;
            if (cat === 'tueren' && ['drehtuer', 'pendeltuer', 'plisseetuer', 'rollo_tuer', 'schiebetuer'].includes(productId)) return true;
            if (cat === 'lichtschacht' && productId === 'lichtschacht') return true;
            return false;
          }),
          name: productNames[productId],
          typ: productNames[productId],
          breite: Number(values[`${productId}_width_${i}`]),
          hoehe: Number(values[`${productId}_height_${i}`]),
          anzahl: 1
        });
      }
      
      return items;
    }).flat();

    setDimensions(products);
    trackKonfiguratorEvent('dimensions_completed', 2, {
      product_count: products.length,
      product_types: selectedProducts
    });
    setActiveStep(3);
    scrollToTop();
  };

  const handleContactSubmit = async (formData: any) => {
    setIsSubmitting(true);
    trackKonfiguratorEvent('form_submit_attempted', 3, {
      product_count: Array.isArray(dimensions) ? dimensions.length : 0
    });

    try {
      const submitData = {
        ...formData,
        produkte: dimensions
      };
      
      const response = await submitKonfigurator(submitData);
      
      if (response.success) {
        await trackKonfiguratorEvent('form_submitted', 3, {
          product_count: Array.isArray(dimensions) ? dimensions.length : 0
        });

        if (response.redirectUrl) {
          window.location.href = response.redirectUrl;
        } else {
          // Reset form
          setActiveStep(0);
          setSelectedCategories([]);
          setSelectedProducts([]);
          setDimensions(null);
          setDraftCategories([]);
          setDraftProducts([]);
          setQuantities({});
        }
      }
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      trackKonfiguratorEvent('form_submit_failed', 3, {
        message: (error as Error).message
      });
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (activeStep === 2 && selectedCategories.length === 1 && selectedCategories[0] === 'lichtschacht') {
      setActiveStep(0);
      setSelectedCategories([]);
      setSelectedProducts([]);
      setDraftCategories([]);
      setQuantities({});
    } else if (activeStep === 2) {
      setActiveStep(1);
      setDraftProducts([]);
      setQuantities({});
    } else if (activeStep === 1) {
      setActiveStep(0);
      setSelectedCategories([]);
      setSelectedProducts([]);
      setDraftCategories([]);
      setDraftProducts([]);
    } else if (activeStep === 3) {
      setActiveStep(2);
    } else {
      setActiveStep((prev) => prev - 1);
    }
    scrollToTop();
  };

  const handleErrorClose = () => {
    setError(null);
  };

  useEffect(() => {
    trackKonfiguratorEvent('configurator_started', 0);
  }, []);

  useEffect(() => {
    if (trackedStepViews.current.has(activeStep)) {
      return;
    }

    trackedStepViews.current.add(activeStep);

    if (activeStep === 2) {
      trackKonfiguratorEvent('dimensions_started', activeStep, {
        product_types: selectedProducts
      });
    }

    if (activeStep === 3) {
      trackKonfiguratorEvent('contact_form_viewed', activeStep, {
        product_count: Array.isArray(dimensions) ? dimensions.length : 0
      });
    }
  }, [activeStep, dimensions, selectedProducts]);

  useEffect(() => {
    const menuToggle = document.querySelector('.hfe-nav-menu__toggle');
    const menuIcon = document.querySelector('.hfe-nav-menu-icon');
    const nav = document.querySelector('.hfe-nav-menu__layout-horizontal');
    
    if (menuToggle && nav) {
      const handleMenuClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Toggle die aktiven Klassen
        menuToggle.classList.toggle('hfe-active-menu');
        menuToggle.classList.toggle('hfe-active-menu-full-width');
        nav.classList.toggle('menu-is-active');
        
        // Ändere das Icon
        if (menuIcon) {
          const icon = menuIcon.querySelector('i');
          if (icon) {
            if (icon.classList.contains('fas')) {
              icon.className = 'far fa-window-close';
            } else {
              icon.className = 'fas fa-align-justify';
            }
          }
        }

        // Setze die Styles für das mobile Menü
        if (nav.classList.contains('menu-is-active')) {
          nav.setAttribute('style', 'width: 390px; left: -232.391px; z-index: 9999; display: block;');
          (menuToggle as HTMLElement).setAttribute('aria-expanded', 'true');
        } else {
          nav.setAttribute('style', '');
          (menuToggle as HTMLElement).setAttribute('aria-expanded', 'false');
        }
      };

      menuToggle.addEventListener('click', handleMenuClick);
      if (menuIcon) {
        menuIcon.addEventListener('click', handleMenuClick);
      }

      return () => {
        menuToggle.removeEventListener('click', handleMenuClick);
        if (menuIcon) {
          menuIcon.removeEventListener('click', handleMenuClick);
        }
      };
    }
  }, []);

  const sidebar = useMemo((): {
    count: number;
    countLabel: string;
    subLabel?: string;
    items: SidebarItem[];
    emptyHint: string;
  } => {
    if (activeStep === 0) {
      return {
        count: draftCategories.length,
        countLabel: 'Bereiche',
        items: draftCategories.map(cat => ({ label: categoryTitles[cat] })),
        emptyHint: 'Noch nichts gewählt. Tippen Sie Bereiche an — Sie können mehrere in einer Anfrage bündeln.',
      };
    }

    if (activeStep === 1) {
      return {
        count: draftProducts.length,
        countLabel: 'Produkte',
        subLabel: draftProducts.length > 0 ? `${draftProducts.length} Produkt(e) vorgemerkt` : undefined,
        items: draftProducts.map(id => ({ label: productNames[id] })),
        emptyHint: 'Noch nichts gewählt. Tippen Sie Produkte an — Sie können mehrere in einer Anfrage bündeln.',
      };
    }

    if (activeStep === 2) {
      const totalPositions = selectedProducts.reduce(
        (sum, id) => sum + (quantities[id] || 1),
        0
      );
      return {
        count: selectedProducts.length,
        countLabel: 'Produkte',
        subLabel: `${totalPositions} Position(en) insgesamt`,
        items: selectedProducts.map(id => ({
          label: productNames[id],
          quantity: quantities[id] || 1,
        })),
        emptyHint: 'Noch nichts gewählt.',
      };
    }

    // Schritt 4: aus den erfassten Positionen ableiten
    const positionItems: SidebarItem[] = [];
    if (Array.isArray(dimensions)) {
      const grouped = new Map<string, number>();
      dimensions.forEach((item: any) => {
        grouped.set(item.name, (grouped.get(item.name) || 0) + 1);
      });
      grouped.forEach((quantity, label) => {
        positionItems.push({ label, quantity });
      });
    }
    return {
      count: positionItems.length,
      countLabel: 'Produkte',
      subLabel: `${Array.isArray(dimensions) ? dimensions.length : 0} Position(en) insgesamt`,
      items: positionItems,
      emptyHint: 'Noch nichts gewählt.',
    };
  }, [activeStep, draftCategories, draftProducts, selectedProducts, quantities, dimensions]);

  return (
    <ThemeProvider theme={theme}>
      <Container 
        maxWidth="lg" 
        className="isk-app"
        sx={{ 
          py: 4,
          position: 'relative'
        }}
        ref={configuratorRef}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Paper 
          elevation={0}
          sx={{ 
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 30px rgba(16, 24, 40, 0.06)',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 4, md: 6 },
              py: { xs: 2, sm: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <WizardHeader steps={steps} activeStep={activeStep} />
          </Box>

          <Grid container>
            <Grid item xs={12} md={8.5} sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
              {activeStep === 0 && (
                <ProductSelection 
                  onCategorySelect={handleCategorySelect}
                  onSelectionChange={(sel) => setDraftCategories(sel as ProductCategory[])}
                  step={0}
                />
              )}
              
              {activeStep === 1 && selectedCategories.length > 0 && (
                <ProductSelection
                  categories={selectedCategories}
                  onNext={handleProductSelect}
                  onBack={handleBack}
                  onSelectionChange={(sel) => setDraftProducts(sel as ProductType[])}
                  step={1}
                />
              )}

              {activeStep === 2 && (
                <Dimensions
                  selectedProducts={selectedProducts}
                  onNext={handleDimensionsSubmit}
                  onBack={handleBack}
                  onQuantitiesChange={setQuantities}
                />
              )}

              {activeStep === 3 && dimensions && (
                <ContactForm
                  onBack={handleBack}
                  onSubmit={handleContactSubmit}
                  isSubmitting={isSubmitting}
                  selectedProducts={dimensions}
                />
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={3.5}
              sx={{
                bgcolor: '#f7f8fa',
                borderLeft: { md: '1px solid #e5e7eb' },
                borderTop: { xs: '1px solid #e5e7eb', md: 'none' },
                p: { xs: 2.5, sm: 3 },
              }}
            >
              <SelectionSidebar
                count={sidebar.count}
                countLabel={sidebar.countLabel}
                subLabel={sidebar.subLabel}
                items={sidebar.items}
                emptyHint={sidebar.emptyHint}
              />
            </Grid>
          </Grid>
        </Paper>

        <Snackbar 
          open={error !== null} 
          autoHideDuration={6000} 
          onClose={handleErrorClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleErrorClose} 
            severity={error === 'success' ? 'success' : 'error'} 
            sx={{ width: '100%' }}
          >
            {error === 'success' 
              ? 'Ihre Anfrage wurde erfolgreich gesendet. Wir werden uns in Kürze bei Ihnen melden.' 
              : error}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default App; 
